import { Injectable } from '@angular/core';

import html2canvas from 'html2canvas';

declare const mermaid: any;

@Injectable({
  providedIn: 'root',
})
export class MermaidConverterService {
  private isInitialized = false;
  private mermaidLoaded = false;

  constructor() {
    this.ensureMermaidLoaded().then(() => {
      this.initializeMermaid();
    });
  }

  private initializeMermaid(): void {
    if (typeof mermaid !== 'undefined' && !this.isInitialized) {
      mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true,
          curve: 'basis',
        },
      });
      this.isInitialized = true;
    } else if (typeof mermaid === 'undefined') {
      // Mermaid library not available
    }
  }

  /**
   * Render entire HTML content with Mermaid diagrams and capture as multiple page images
   * @param htmlContent - The full HTML content to render
   * @returns Promise<string[]> - Array of base64 image data URLs (one per page)
   */
  async renderHtmlContentAsImages(htmlContent: string): Promise<string[]> {
    // Ensure Mermaid is loaded first
    await this.ensureMermaidLoaded();

    if (typeof mermaid === 'undefined') {
      throw new Error('Mermaid library not available');
    }

    return new Promise((resolve, reject) => {
      try {
        // Create a temporary container for rendering
        const tempContainer = document.createElement('div');
        tempContainer.style.position = 'absolute';
        tempContainer.style.top = '0';
        tempContainer.style.left = '0';
        tempContainer.style.width = '794px'; // A4 width in pixels (210mm)
        tempContainer.style.backgroundColor = 'white';
        tempContainer.style.padding = '76px'; // 20mm in pixels
        tempContainer.style.fontFamily = 'Arial, sans-serif';
        tempContainer.style.fontSize = '14px';
        tempContainer.style.lineHeight = '1.6';
        tempContainer.style.visibility = 'visible';
        tempContainer.style.zIndex = '9999';
        tempContainer.style.boxSizing = 'border-box';

        // Insert the HTML content
        tempContainer.innerHTML = htmlContent;

        document.body.appendChild(tempContainer);

        // Initialize Mermaid if not already done
        this.initializeMermaid();

        // Process all mermaid diagrams in the container
        const mermaidElements = tempContainer.querySelectorAll(
          '.mermaid, code.language-mermaid, pre code.language-mermaid'
        );

        if (mermaidElements.length === 0) {
          // No Mermaid diagrams, just capture pages
          this.captureContainerAsMultiplePages(tempContainer, resolve, reject);
        } else {
          // Render Mermaid diagrams first, then capture
          mermaid.init(undefined, mermaidElements);

          // Wait for Mermaid to finish rendering
          setTimeout(() => {
            this.captureContainerAsMultiplePages(tempContainer, resolve, reject);
          }, 1000);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  private async captureContainerAsMultiplePages(
    container: HTMLElement,
    resolve: (value: string[]) => void,
    reject: (reason?: any) => void
  ): Promise<void> {
    try {
      const pageHeight = 1123; // A4 height in pixels (297mm) minus padding
      const containerHeight = container.scrollHeight;
      const pages: string[] = [];

      // Calculate number of pages needed
      const numPages = Math.ceil(containerHeight / pageHeight);

      for (let pageIndex = 0; pageIndex < numPages; pageIndex++) {
        // Calculate the viewport for this page
        const startY = pageIndex * pageHeight;

        // Create a clipped canvas for this page
        const canvas = await html2canvas(container, {
          backgroundColor: 'white',
          scale: 1.5, // Good balance of quality and file size
          useCORS: true,
          allowTaint: true,
          width: container.offsetWidth,
          height: pageHeight,
          scrollX: 0,
          scrollY: startY,
          windowWidth: container.offsetWidth,
          windowHeight: pageHeight,
        });

        // Convert to base64 image
        const imageDataUrl = canvas.toDataURL('image/png', 0.9);
        pages.push(imageDataUrl);
      }

      // Clean up the temporary element
      document.body.removeChild(container);

      resolve(pages);
    } catch (error) {
      // Clean up on error
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
      reject(error);
    }
  }

  private async captureContainerAsImage(
    container: HTMLElement,
    resolve: (value: string) => void,
    reject: (reason?: any) => void
  ): Promise<void> {
    try {
      // Capture the entire container with html2canvas
      const canvas = await html2canvas(container, {
        backgroundColor: 'white',
        scale: 1.5, // High quality but manageable file size
        useCORS: true,
        allowTaint: true,
        width: container.offsetWidth,
        height: container.offsetHeight,
        scrollX: 0,
        scrollY: 0,
      });

      // Convert to base64 image
      const imageDataUrl = canvas.toDataURL('image/png', 0.9);

      // Clean up the temporary element
      document.body.removeChild(container);

      resolve(imageDataUrl);
    } catch (error) {
      // Clean up on error
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
      reject(error);
    }
  }

  /**
   * Convert Mermaid diagrams in markdown text to images (for PDF export)
   * @param markdownText - The markdown text containing Mermaid diagrams
   * @param forPdf - Whether this is for PDF export (uses images instead of SVG)
   * @returns Promise<string> - The markdown text with Mermaid diagrams replaced by images/SVG
   */
  async convertMermaidToSvg(markdownText: string, forPdf = false): Promise<string> {
    // Ensure Mermaid is loaded first
    await this.ensureMermaidLoaded();

    if (!markdownText || typeof mermaid === 'undefined') {
      return markdownText;
    }

    this.initializeMermaid();

    // Find all Mermaid code blocks
    const mermaidRegex = /```mermaid\n([\s\S]*?)```/g;
    let result = markdownText;
    const matches = Array.from(markdownText.matchAll(mermaidRegex));

    for (let i = 0; i < matches.length; i++) {
      const match = matches[i];
      const mermaidCode = match[1].trim();

      if (!mermaidCode) continue;

      try {
        // Generate unique ID for each diagram
        const diagramId = `mermaid-diagram-${Date.now()}-${i}`;

        if (forPdf) {
          // For PDF export, convert to image
          const imageCode = await this.renderMermaidToImage(mermaidCode, diagramId);
          if (imageCode) {
            result = result.replace(match[0], imageCode);
          }
        } else {
          // For preview, use SVG
          const svgCode = await this.renderMermaidToSvg(mermaidCode, diagramId);
          if (svgCode) {
            result = result.replace(match[0], svgCode);
          }
        }
      } catch (error) {
        // Keep the original Mermaid block if conversion fails
      }
    }

    return result;
  }

  /**
   * Ensure Mermaid library is loaded
   */
  private async ensureMermaidLoaded(): Promise<void> {
    if (this.mermaidLoaded && typeof mermaid !== 'undefined') {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      // Check if already loaded
      if (typeof mermaid !== 'undefined') {
        this.mermaidLoaded = true;
        resolve();
        return;
      }

      // Try to load from CDN as fallback
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/mermaid@9.4.3/dist/mermaid.min.js';
      script.onload = () => {
        this.mermaidLoaded = true;
        resolve();
      };
      script.onerror = () => {
        resolve(); // Resolve anyway to prevent hanging
      };

      document.head.appendChild(script);

      // Timeout fallback
      setTimeout(() => {
        if (!this.mermaidLoaded) {
          resolve();
        }
      }, 5000);
    });
  }

  /**
   * Render a single Mermaid diagram to SVG
   * @param mermaidCode - The Mermaid diagram code
   * @param elementId - Unique ID for the diagram
   * @returns Promise<string> - The generated SVG code
   */
  private async renderMermaidToSvg(mermaidCode: string, elementId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        // Validate Mermaid syntax first
        const isValid = mermaid.parse(mermaidCode);
        if (!isValid) {
          reject(new Error('Invalid Mermaid syntax'));
          return;
        }

        // Create a temporary div element for rendering
        const tempDiv = document.createElement('div');
        tempDiv.id = elementId;
        tempDiv.style.visibility = 'hidden';
        tempDiv.style.position = 'absolute';
        tempDiv.style.top = '-9999px';
        document.body.appendChild(tempDiv);

        // Render the diagram
        mermaid.render(
          elementId,
          mermaidCode,
          (svgCode: string) => {
            // Clean up the temporary element
            document.body.removeChild(tempDiv);

            if (svgCode) {
              // Wrap the SVG in a div with proper styling for PDF
              const wrappedSvg = `
              <div class="mermaid-diagram" style="text-align: center; margin: 20px 0; page-break-inside: avoid;">
                ${svgCode}
              </div>
            `;
              resolve(wrappedSvg);
            } else {
              reject(new Error('Failed to generate SVG'));
            }
          },
          tempDiv
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Render a single Mermaid diagram to image for PDF export
   * @param mermaidCode - The Mermaid diagram code
   * @param elementId - Unique ID for the diagram
   * @returns Promise<string> - The generated image HTML
   */
  private async renderMermaidToImage(mermaidCode: string, elementId: string): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        // Validate Mermaid syntax first
        const isValid = mermaid.parse(mermaidCode);
        if (!isValid) {
          reject(new Error('Invalid Mermaid syntax'));
          return;
        }

        // Create a temporary div element for rendering
        const tempDiv = document.createElement('div');
        tempDiv.id = elementId;
        tempDiv.style.visibility = 'visible';
        tempDiv.style.position = 'absolute';
        tempDiv.style.top = '0';
        tempDiv.style.left = '0';
        tempDiv.style.backgroundColor = 'white';
        tempDiv.style.padding = '20px';
        tempDiv.style.border = '1px solid #ccc';
        document.body.appendChild(tempDiv);

        // Render the diagram
        mermaid.render(
          elementId,
          mermaidCode,
          async (svgCode: string) => {
            try {
              // Insert SVG into temp div
              tempDiv.innerHTML = svgCode;

              // Wait a bit for SVG to fully render
              await new Promise(resolve => setTimeout(resolve, 500)); // Increased wait time

              // Capture as image
              const canvas = await html2canvas(tempDiv, {
                backgroundColor: 'white',
                scale: 2, // Higher quality
                useCORS: true,
                allowTaint: true,
              });

              // Convert to base64 image
              const imageDataUrl = canvas.toDataURL('image/png');

              // Clean up the temporary element
              document.body.removeChild(tempDiv);

              // Create image HTML with proper styling for PDF
              const imageHtml = `
                <div class="mermaid-diagram" style="text-align: center; margin: 20px 0; page-break-inside: avoid;">
                  <img src="${imageDataUrl}" style="max-width: 100%; height: auto;" alt="Mermaid Diagram" />
                </div>
              `;

              resolve(imageHtml);
            } catch (error) {
              // Clean up on error
              if (document.body.contains(tempDiv)) {
                document.body.removeChild(tempDiv);
              }
              reject(error);
            }
          },
          tempDiv
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Convert data URL to blob (inspired by message-controls.component.ts)
   * @param dataUrl - Base64 data URL
   * @returns Blob
   */
  dataUrlToBlob(dataUrl: string): Blob {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  }

  /**
   * Convert blob to data URL (from message-controls.component.ts)
   * @param blob - Blob to convert
   * @returns Promise<string> - Data URL
   */
  blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Check if Mermaid library is available
   * @returns boolean - True if Mermaid is available
   */
  isMermaidAvailable(): boolean {
    return typeof mermaid !== 'undefined';
  }
}
